const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];

/**
 * Photo upload field (blocks/puf-foto.liquid). The file input belongs to the theme's product
 * form, so the browser's own validation blocks add-to-cart while the custom validity message is
 * set: wrong file type or a file over the size limit. Shows the chosen file's name and a small
 * thumbnail (HEIC files have no browser preview, only the name).
 *
 * Live preview (optional, block settings preview_asset + preview_areas): the names and date the customer
 * types are drawn onto a blank product image, each text box mapped with a perspective transform onto the
 * four image corners where the printed text sits (exported by araclar/render_model.py "gizle").
 */
class PufFoto extends HTMLElement {
  /** @type {string | undefined} */
  #thumbUrl;

  /** @type {AbortController | undefined} */
  #abort;

  /** @type {ResizeObserver | undefined} */
  #resize;

  /** @type {{ el: HTMLElement, icerik: string, dortgen: number[][] }[]} */
  #areas = [];

  /** @type {number[]} */
  #size = [1000, 1000];

  /** @type {Record<string, any>} */
  #config = {};

  connectedCallback() {
    this.#abort = new AbortController();
    const input = this.#input;
    if (!input) return;
    input.addEventListener('change', () => this.#check(), { signal: this.#abort.signal });
    input.addEventListener('invalid', () => this.#showError(), { signal: this.#abort.signal });
    if (input.files?.length) this.#check();
    this.#setupPreview();
    if (this.dataset.tabansiz || this.querySelector('[data-yalniz]')) {
      document.addEventListener('change', () => this.#updateBase(), { signal: this.#abort.signal });
      this.#updateBase();
    }
  }

  /**
   * Seçili ürün seçeneği değerlerinden biri `value` mı (ürün seçenekleri ayrı bir blokta: aynı bölümde aranır).
   * @param {string} value
   */
  #optionChecked(value) {
    const section = this.closest('.shopify-section') || document;
    return [...section.querySelectorAll('input[type="radio"]:checked, select')].some(
      (field) => (field instanceof HTMLInputElement || field instanceof HTMLSelectElement) && field.value === value
    );
  }

  /**
   * Alanı gizler ya da gösterir; gizli alanın girişleri kapatılır, sepete eklenirken gönderilmez.
   * @param {Element} area
   * @param {boolean} hide
   */
  #toggleArea(area, hide) {
    if (!(area instanceof HTMLElement)) return;
    area.hidden = hide;
    area.querySelectorAll('input, textarea').forEach((field) => {
      if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) field.disabled = hide;
    });
  }

  /**
   * Tabansız ürün seçeneği seçiliyken (blok ayarı no_base_value) tabana ait alanlar gizlenir ve kapatılır:
   * kapalı alanlar sepete eklenirken gönderilmez, siparişe isim/tarih/taban rengi geçmez.
   */
  #updateBase() {
    const noBase = Boolean(this.dataset.tabansiz) && this.#optionChecked(this.dataset.tabansiz || '');
    for (const area of this.querySelectorAll('[data-taban-alani]')) this.#toggleArea(area, noBase);
    const note = this.querySelector('[data-tabansiz-not]');
    if (note instanceof HTMLElement) note.hidden = !noBase;
    // Yalnız belirli bir seçenekte görünen alanlar (ör. "Figürler: ayrı ayrı" yalnız boyamaya hazırda).
    for (const area of this.querySelectorAll('[data-yalniz]')) {
      if (area instanceof HTMLElement) this.#toggleArea(area, !this.#optionChecked(area.dataset.yalniz || ''));
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#resize?.disconnect();
    this.#revoke();
  }

  #setupPreview() {
    const frame = this.querySelector('[data-preview-frame]');
    const source = this.querySelector('[data-preview-areas]');
    if (!(frame instanceof HTMLElement) || !source) return;
    let config;
    try {
      config = JSON.parse(JSON.parse(source.textContent || '""'));
    } catch {
      return;
    }
    if (!config || !Array.isArray(config.alanlar)) return;
    this.#config = config;
    if (Array.isArray(config.boyut)) this.#size = config.boyut;
    const stage = frame.querySelector('[data-preview-stage]') || frame;
    frame.querySelectorAll('.puf-foto__preview-text').forEach((el) => el.remove());
    this.#areas = [];
    for (const area of config.alanlar) {
      if (!Array.isArray(area.dortgen) || area.dortgen.length !== 4) continue;
      const el = document.createElement('div');
      el.className = 'puf-foto__preview-text';
      el.setAttribute('aria-hidden', 'true');
      el.dataset.renk = area.renk || config.renk || '#1e1e1e';
      stage.append(el);
      this.#areas.push({ el, icerik: String(area.icerik || 'isim'), dortgen: area.dortgen });
    }
    const signal = this.#abort?.signal;
    for (const id of ['Names-', 'Date-']) {
      const field = this.querySelector(`[id^="${id}"]`);
      field?.addEventListener('input', () => this.#drawPreview(), { signal });
    }
    // Taban rengi ve baskı türü (ürün seçeneği) değişince önizleme görseli ve yazı rengi değişir.
    document.addEventListener('change', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.type === 'radio') this.#updateVariant();
    }, { signal });
    const zoom = this.querySelector('[data-preview-zoom]');
    zoom?.addEventListener('click', () => this.#toggleZoom(), { signal });
    frame.addEventListener('click', () => this.#toggleZoom(), { signal });
    this.#updateVariant();
    this.#resize = new ResizeObserver(() => this.#drawPreview());
    this.#resize.observe(frame);
    document.fonts?.ready.then(() => this.#drawPreview());
    this.#drawPreview();
  }

  #toggleZoom() {
    const frame = this.querySelector('[data-preview-frame]');
    const stage = this.querySelector('[data-preview-stage]');
    const button = this.querySelector('[data-preview-zoom]');
    if (!(frame instanceof HTMLElement) || !(stage instanceof HTMLElement)) return;
    const zoomed = !frame.hasAttribute('data-yakin');
    frame.toggleAttribute('data-yakin', zoomed);
    if (zoomed) {
      // Yazıların ortası (görselin yüzdesi olarak) büyütmenin merkezi olur.
      const points = this.#areas.flatMap((area) => area.dortgen);
      const w = this.#size[0] || 1000;
      const h = this.#size[1] || 1000;
      const cx = points.reduce((sum, p) => sum + p[0], 0) / (points.length || 1) / w;
      const cy = points.reduce((sum, p) => sum + p[1], 0) / (points.length || 1) / h;
      stage.style.transformOrigin = `${(cx * 100).toFixed(2)}% ${(cy * 100).toFixed(2)}%`;
      stage.style.transform = `scale(${Number(this.#config.yakinlik) || 2.4})`;
    } else {
      stage.style.transform = '';
    }
    if (button) {
      const strings = this.#strings;
      button.setAttribute('aria-pressed', String(zoomed));
      button.textContent = (zoomed ? strings.zoomOut : strings.zoomIn) || button.textContent;
    }
  }

  #updateVariant() {
    const checkedBase = this.querySelector('[data-base-color]:checked');
    const base = checkedBase instanceof HTMLInputElement ? checkedBase.value : 'Beyaz';
    const section = this.closest('.shopify-section') || document;
    const paint = section.querySelector('input[type="radio"][value^="Boyamaya"]:checked');
    const kind = paint ? 'boyama' : 'renkli';
    const images = this.#config.gorseller || {};
    const file = images[`${kind}|${base}`] || images[`${kind}|Beyaz`] || images[`renkli|${base}`];
    const img = this.querySelector('[data-preview-img]');
    if (img instanceof HTMLImageElement) {
      if (!img.dataset.ilk) img.dataset.ilk = img.getAttribute('src') || '';
      let src = img.dataset.ilk;
      if (file) {
        const url = new URL(img.dataset.ilk, window.location.href);
        url.pathname = url.pathname.replace(/[^/]+$/, file);
        url.search = '';
        src = url.toString();
      }
      if (img.getAttribute('src') !== src) img.setAttribute('src', src);
    }
    const colours = this.#config.yazi_renk || {};
    for (const { el } of this.#areas) el.style.color = colours[base] || el.dataset.renk || '#1e1e1e';
  }

  /** @param {string} prefix */
  #fieldText(prefix) {
    const field = this.querySelector(`[id^="${prefix}"]`);
    if (!(field instanceof HTMLInputElement)) return { text: '', example: false };
    const typed = field.value.trim();
    return typed ? { text: typed, example: false } : { text: field.placeholder.trim(), example: true };
  }

  #drawPreview() {
    const frame = this.querySelector('[data-preview-frame]');
    if (!(frame instanceof HTMLElement) || !this.#areas.length) return;
    const scale = frame.clientWidth / (this.#size[0] || 1000);
    const names = this.#fieldText('Names-');
    const date = this.#fieldText('Date-');
    const parts = names.text.split(/\s*(?:&|,|\+|\sve\s)\s*/i).filter(Boolean);

    for (const { el, icerik, dortgen } of this.#areas) {
      /** @type {{ text: string, share: number }[]} */
      let lines = [];
      let example = names.example;
      if (icerik === 'isim') lines = [{ text: names.text, share: 0.86 }];
      else if (icerik === 'isim1') lines = [{ text: parts[0] || '', share: 0.86 }];
      else if (icerik === 'isim2') lines = [{ text: parts.slice(1).join(' ') || '', share: 0.86 }];
      else if (icerik === 'tarih') {
        lines = [{ text: date.text, share: 0.86 }];
        example = date.example;
      } else {
        // Örnek tarih yalnızca isim de örnekken görünür; isim yazılıp tarih boş bırakıldıysa tarih basılmaz.
        const withDate = date.text && (!date.example || names.example);
        lines = withDate
          ? [{ text: names.text, share: 0.56 }, { text: date.text, share: 0.32 }]
          : [{ text: names.text, share: 0.8 }];
        example = names.example && (date.example || !withDate);
      }

      const quad = dortgen.map(([x, y]) => [x * scale, y * scale]);
      const top = Math.hypot(quad[1][0] - quad[0][0], quad[1][1] - quad[0][1]);
      const bottom = Math.hypot(quad[2][0] - quad[3][0], quad[2][1] - quad[3][1]);
      const left = Math.hypot(quad[3][0] - quad[0][0], quad[3][1] - quad[0][1]);
      const right = Math.hypot(quad[2][0] - quad[1][0], quad[2][1] - quad[1][1]);
      // Düz kutu: 400 px en, yüksekliği köşelerin en/boy oranında; sonra köşelere eğilir.
      const width = 400;
      const height = Math.max(20, (width * (left + right)) / Math.max(1, top + bottom));
      el.style.width = `${width}px`;
      el.style.height = `${height}px`;
      el.toggleAttribute('data-ornek', example);
      el.replaceChildren(
        ...lines
          .filter((line) => line.text)
          .map((line) => {
            const span = document.createElement('span');
            span.textContent = line.text;
            span.style.fontSize = `${height * line.share}px`;
            return span;
          })
      );
      for (const span of el.children) {
        if (!(span instanceof HTMLElement)) continue;
        const over = span.scrollWidth / (width * 0.98);
        if (over > 1) span.style.fontSize = `${parseFloat(span.style.fontSize) / over}px`;
      }
      el.style.transform = quadTransform(quad, width, height);
    }
  }

  get #input() {
    const input = this.querySelector('[data-photo]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** @returns {Record<string, string>} */
  get #strings() {
    try {
      return JSON.parse(this.querySelector('[data-strings]')?.textContent || '{}');
    } catch {
      return {};
    }
  }

  #revoke() {
    if (this.#thumbUrl) URL.revokeObjectURL(this.#thumbUrl);
    this.#thumbUrl = undefined;
  }

  /** @param {File} file */
  #isImage(file) {
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    return IMAGE_TYPES.includes(file.type) || (file.type === '' && IMAGE_EXTENSIONS.includes(extension));
  }

  #check() {
    const input = this.#input;
    if (!input) return;
    const strings = this.#strings;
    const file = input.files?.[0];
    const name = this.querySelector('[data-file-name]');
    const thumb = this.querySelector('[data-thumb]');
    const maxBytes = Number(this.dataset.maxMb || 15) * 1024 * 1024;

    this.#revoke();
    if (thumb instanceof HTMLImageElement) {
      thumb.hidden = true;
      thumb.removeAttribute('src');
    }

    let message = '';
    if (file && !this.#isImage(file)) message = strings.wrongType || '';
    else if (file && file.size > maxBytes) message = strings.tooBig || '';
    input.setCustomValidity(message);
    this.#setError(message);

    if (!file || message) {
      if (name) name.textContent = strings.choose || '';
      if (message) input.value = '';
      return;
    }

    if (name) name.textContent = `${strings.selected || ''} ${file.name}`.trim();
    if (thumb instanceof HTMLImageElement && file.type && file.type !== 'image/heic' && file.type !== 'image/heif') {
      this.#thumbUrl = URL.createObjectURL(file);
      thumb.src = this.#thumbUrl;
      thumb.hidden = false;
    }
  }

  #showError() {
    const input = this.#input;
    if (input) this.#setError(input.validationMessage);
  }

  /** @param {string} message */
  #setError(message) {
    const error = this.querySelector('[data-error]');
    if (error) error.textContent = message;
  }
}

/**
 * CSS matrix3d that maps a width x height box (origin top-left) onto a quad given as
 * [top-left, top-right, bottom-right, bottom-left] corners in the same pixel space.
 * @param {number[][]} quad
 * @param {number} width
 * @param {number} height
 */
function quadTransform(quad, width, height) {
  const [[x0, y0], [x1, y1], [x2, y2], [x3, y3]] = quad;
  const dx1 = x1 - x2;
  const dx2 = x3 - x2;
  const dy1 = y1 - y2;
  const dy2 = y3 - y2;
  const sx = x0 - x1 + x2 - x3;
  const sy = y0 - y1 + y2 - y3;
  let g = 0;
  let h = 0;
  if (Math.abs(sx) > 1e-9 || Math.abs(sy) > 1e-9) {
    const den = dx1 * dy2 - dx2 * dy1 || 1e-9;
    g = (sx * dy2 - dx2 * sy) / den;
    h = (dx1 * sy - sx * dy1) / den;
  }
  const a = x1 - x0 + g * x1;
  const b = x3 - x0 + h * x3;
  const d = y1 - y0 + g * y1;
  const e = y3 - y0 + h * y3;
  const m = [a / width, d / width, 0, g / width, b / height, e / height, 0, h / height, 0, 0, 1, 0, x0, y0, 0, 1];
  return `matrix3d(${m.map((v) => +v.toFixed(8)).join(',')})`;
}

if (!customElements.get('puf-foto')) customElements.define('puf-foto', PufFoto);
